import Pane from 'components/Common/Pane'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import { EmptyArray } from 'lib/array/arrayUtils'
import { getLoyaltyOffersHubHeroCarouselItemId } from 'loyalty/lib/loyaltyOffersHub/getLoyaltyOffersHubHeroCarouselItemId'
import type { LoyaltyOffersHubHeroCarouselItem } from 'loyalty/lib/loyaltyOffersHub/types'
import type { ImageCarouselInterstitial } from 'LuxKit/Carousel/BaseImageCarousel'
import ImageCarousel from 'LuxKit/Carousel/ImageCarousel'
import { rem } from 'polished'
import type { ComponentProps } from 'react'
import { useMemo } from 'react'
import styled from 'styled-components'
import LoyaltyOffersHubHeroCarouselCard from './LoyaltyOffersHubHeroCarouselCard'

const HeroCard = styled(Pane)`
  position: relative;
  min-width: ${rem(280)};
  overflow: hidden;
`

const HeroCarouselLayout = styled.div`
  position: relative;
  display: grid;
`

const HiddenSizingContent = styled.div`
  display: grid;
  grid-area: 1 / 1;
  visibility: hidden;
  pointer-events: none;

  > * {
    grid-area: 1 / 1;
  }
`

const VisibleCarouselContent = styled.div`
  position: absolute;
  inset: 0;
  grid-area: 1 / 1;
`

type HeroCarouselArrowBehaviour = NonNullable<ComponentProps<typeof ImageCarousel>['arrowBehaviour']>

interface Props {
  items: Array<LoyaltyOffersHubHeroCarouselItem>
}

interface HeroCarouselContentProps extends Props {
  arrowBehaviour: HeroCarouselArrowBehaviour
}

function LoyaltyOffersHubHeroCarouselContent({ arrowBehaviour, items }: HeroCarouselContentProps) {
  const sizingCards = useMemo(() => {
    return items.map(item => <LoyaltyOffersHubHeroCarouselCard
      key={getLoyaltyOffersHubHeroCarouselItemId(item)}
      item={item}
      useImagePlaceholder
    />)
  }, [items])

  const slides = useMemo<Array<ImageCarouselInterstitial>>(
    () => {
      return items.map((item, index) => ({
        id: getLoyaltyOffersHubHeroCarouselItemId(item),
        position: index + 1,
        element: <LoyaltyOffersHubHeroCarouselCard
          item={item}
        />,
      }))
    }, [items])

  return <HeroCard borderRadius="L" background="normal-background">
    <HeroCarouselLayout>
      <HiddenSizingContent aria-hidden inert>
        {sizingCards}
      </HiddenSizingContent>
      <VisibleCarouselContent>
        <ImageCarousel
          images={EmptyArray}
          interstitials={slides}
          autoScrollSpeed={5000}
          arrowStyle="ghost"
          arrowBehaviour={arrowBehaviour}
          eagerLoadFirstImage
          wrapBehaviour="rewind"
        />
      </VisibleCarouselContent>
    </HeroCarouselLayout>
  </HeroCard>
}

function LoyaltyOffersHubHeroCarousel({ items }: Props) {
  if (!items.length) return null

  return <>
    <CSSBreakpoint max="mobile" dynamic>
      <LoyaltyOffersHubHeroCarouselContent
        items={items}
        arrowBehaviour="hidden"
      />
    </CSSBreakpoint>
    <CSSBreakpoint min="tablet" dynamic>
      <LoyaltyOffersHubHeroCarouselContent
        items={items}
        arrowBehaviour="interaction-visible"
      />
    </CSSBreakpoint>
  </>
}

export default LoyaltyOffersHubHeroCarousel
